import { CmsProductGtinWithSlug } from "components/cart/common/types";
import { ITEM_BRAND } from "config";
import {
  logProductListImpressionEvent,
  logSelectItemEvent,
} from "helpers/analyticsLogger";
import { useEffect } from "react";
import {
  BundleLineItemFragment,
  CartFragment,
  CartItemFragment,
} from "services/graphql/generated";

const itemListName = "Cart";

export const useCartAnalytics = (
  currency: string,
  cart: CartFragment,
  cmsProducts?: CmsProductGtinWithSlug[],
) => {
  const readyCartItemsForLogging = (
    lineItems: (BundleLineItemFragment | CartItemFragment)[],
    currency: string,
    itemListName?: string,
  ) => {
    const getItemDetails = (lineItem: any) => {
      if (lineItem.__typename === "BundleLineItem") {
        return {
          item: lineItem?.primaryBundleItems?.[0]?.item || null,
          price: lineItem?.subtotal ?? 0,
          bundleTag: lineItem?.dynamicBundle?.tag,
        };
      } else if (lineItem.__typename === "CartItem") {
        return {
          item: lineItem?.item || null,
          price: lineItem?.item?.price ?? 0,
        };
      }
      return {
        item: null,
        price: null,
      };
    };

    return lineItems.map((lineItem, index) => {
      const {
        item: { gtin, id, title },
        price,
        bundleTag,
      } = getItemDetails(lineItem);

      const category =
        lineItem.__typename === "BundleLineItem"
          ? (cmsProducts?.find((product) =>
              product.bundleTags?.includes(bundleTag),
            )?.category?.analyticsCategoryName ?? "")
          : (cmsProducts?.find((item) => item.gtin === gtin)?.category
              ?.analyticsCategoryName ?? "");

      const itemId =
        lineItem.__typename === "BundleLineItem" ? lineItem.bundleId : id;
      const itemName =
        lineItem.__typename === "BundleLineItem" ? lineItem.title : title;

      return {
        currency,
        gtin,
        itemCategory: category,
        itemId,
        index: index + 1,
        itemBrand: ITEM_BRAND,
        itemListName,
        itemName,
        price: price ? price / 100 : 0,
      };
    });
  };

  useEffect(() => {
    if (cart.lineItems.length && currency && cmsProducts?.length) {
      const items = readyCartItemsForLogging(cart.lineItems, currency);
      logProductListImpressionEvent(itemListName, currency, items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, cart.lineItems, cmsProducts]);

  const handleCartItemClickLogging = (
    item: BundleLineItemFragment | CartItemFragment,
  ) => {
    const items = readyCartItemsForLogging([item], currency, itemListName);
    logSelectItemEvent(itemListName, currency, items);
  };

  return { handleCartItemClickLogging };
};
